import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Pageable } from '../../models/common/pageable.model';
import {
  ProductTagTypeCrudCreateModel,
  ProductTagTypeCrudUpdateModel,
  ProductTagTypeListModel
} from '../../models/tagging/product-tag-type.model';
import {ProductTagListModel} from '../../models/tagging/product-tagging.model';

export interface ProductTagsSearchParams {
  productId?: number;
  refId?: string;
  skuId?: number;
  productName?: string;
  tagTypeId?: number;
  page?: number;
  pageSize?: number;
  order?: 'ASC' | 'DESC';
}

@Injectable({ providedIn: 'root' })
export class TagsService {
  private readonly baseUrl = `${environment.backend.url}/api/tags/v1`;

  constructor(private readonly http: HttpClient) {}

  listTypes(page: number = 1, pageSize: number = 10): Observable<Pageable<ProductTagTypeListModel>> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());
    return this.http.get<Pageable<ProductTagTypeListModel>>(`${this.baseUrl}/type/list`, { params });
  }

  getTagType(id: number): Observable<ProductTagTypeCrudUpdateModel> {
    return this.http.get<ProductTagTypeCrudUpdateModel>(`${this.baseUrl}/type/by-id/${id}`);
  }

  createType(data: ProductTagTypeCrudCreateModel): Observable<{ id: number }> {
    return this.http.post<{ id: number }>(`${this.baseUrl}/type/create`, data);
  }

  updateType(data: ProductTagTypeCrudUpdateModel): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/type/update`, data);
  }

  /**
   * Fetches a paginated list of product tags from the server.
   *
   * @param params Search parameters (filters, pagination, order).
   * @return Observable containing the paginated list of product tags.
   */
  getProductTags(paramsOrPage?: ProductTagsSearchParams | number, pageSize: number = 10): Observable<Pageable<ProductTagListModel>> {
    const page = typeof paramsOrPage === 'number' ? paramsOrPage : (paramsOrPage?.page ?? 0);
    const size = typeof paramsOrPage === 'number' ? pageSize : (paramsOrPage?.pageSize ?? pageSize);
    const filters = typeof paramsOrPage === 'object' ? paramsOrPage : undefined;

    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', size.toString());

    if (filters) {
      if (filters.productId != null) params = params.set('productId', String(filters.productId));
      if (filters.refId != null && filters.refId !== '') params = params.set('refId', filters.refId);
      if (filters.skuId != null) params = params.set('skuId', String(filters.skuId));
      if (filters.productName != null && filters.productName !== '') params = params.set('productName', filters.productName);
      if (filters.tagTypeId != null) params = params.set('tagTypeId', String(filters.tagTypeId));
      if (filters.order != null) params = params.set('order', filters.order);
    }

    return this.http.get<Pageable<ProductTagListModel>>(`${this.baseUrl}/list`, { params });
  }
}
